<template>
  <div class="page-wrapper">

    <!-- 会员套餐信息卡 -->
    <div class="profile-card membership-card" v-loading="loading">
      <p class="card-desc">您当前的会员套餐与状态</p>
      <div class="membership-grid">
        <div class="membership-item">
          <span class="m-label">会员类型</span>
          <span class="tag" :class="`tag-${memberInfo.typeKey}`">{{ memberInfo.typeLabel || '—' }}</span>
        </div>
        <div class="membership-item">
          <span class="m-label">当前状态</span>
          <span class="status-badge" :class="memberInfo.active ? 'badge-active' : 'badge-expired'">
            {{ memberInfo.active ? '有效' : '已过期' }}
          </span>
        </div>
        <div class="membership-item">
          <span class="m-label">注册日期</span>
          <span class="m-value">{{ memberInfo.regDate || '—' }}</span>
        </div>
        <div class="membership-item">
          <span class="m-label">到期日期</span>
          <span class="m-value" :class="{ 'near-expire': memberInfo.nearExpire, 'expired-text': !memberInfo.active }">
            {{ memberInfo.expireDate || '—' }}
          </span>
        </div>
      </div>
    </div>

    <!-- 待上课程（已确认预约） -->
    <div class="profile-card appt-card">
      <div class="card-title-row">
        <p class="card-desc" style="margin:0">待上课程</p>
        <span class="link-all" @click="$router.push('/appointment')">查看全部 →</span>
      </div>
      <div v-if="!apptLoading && recentAppts.length === 0" class="empty-tip">暂无待上课程，<span class="link-all" @click="$router.push('/appointment')">立即预约</span></div>
      <div v-else class="appt-list" v-loading="apptLoading">
        <div class="appt-item" v-for="a in recentAppts" :key="a.appointmentId">
          <div class="appt-dot" :class="`dot-${a.statusKey}`"></div>
          <div class="appt-body">
            <p class="appt-course">{{ a.courseName }}</p>
            <p class="appt-meta">{{ a.coachName }} · {{ a.dateStr }}</p>
          </div>
          <span class="tag appt-tag" :class="`tag-status-${a.statusKey}`">{{ a.statusLabel }}</span>
        </div>
      </div>
    </div>

    <!-- 个人资料编辑卡 -->
    <div class="profile-card">
      <p class="card-desc">修改您的资料、头像与登录密码</p>

      <el-form v-loading="loading"
        :label-position="isMobile ? 'top' : 'left'"
        :label-width="isMobile ? 'auto' : '100px'"
        class="profile-form" @submit.prevent>
        <el-form-item label="头像">
          <div class="avatar-row">
            <div class="avatar-preview">
              <img v-if="form.avatar" :src="avatarDisplayUrl" alt="" />
              <span v-else>{{ (form.username || '会').charAt(0) }}</span>
            </div>
            <el-upload
              :auto-upload="false"
              :show-file-list="false"
              accept="image/png,image/jpeg,image/gif,image/webp"
              :on-change="onAvatarChange"
            >
              <el-button type="primary" plain size="small">更换头像</el-button>
            </el-upload>
            <el-button v-if="form.avatar" type="danger" plain size="small" @click="form.avatar = ''">清除</el-button>
          </div>
          <p class="hint">建议小于 500KB</p>
        </el-form-item>

        <el-form-item label="用户名">
          <el-input v-model="form.username" placeholder="登录用户名" maxlength="32" clearable />
        </el-form-item>

        <el-form-item label="姓名">
          <el-input v-model="form.name" placeholder="真实姓名" maxlength="32" clearable />
        </el-form-item>

        <el-form-item label="手机号">
          <el-input v-model="form.phone" placeholder="手机号" maxlength="20" clearable />
        </el-form-item>

        <el-form-item label="邮箱">
          <el-input v-model="form.email" placeholder="邮箱" maxlength="64" clearable />
        </el-form-item>

        <el-divider content-position="left">修改密码（不修改请留空）</el-divider>

        <el-form-item label="原密码">
          <el-input v-model="form.oldPassword" type="password" show-password placeholder="修改密码时必填" autocomplete="off" />
        </el-form-item>
        <el-form-item label="新密码">
          <el-input v-model="form.newPassword" type="password" show-password placeholder="至少 6 位" autocomplete="new-password" />
        </el-form-item>

        <el-form-item class="save-item">
          <el-button type="primary" :loading="saving" class="save-btn" @click="save">保存</el-button>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import api from '@/api/request'
import { resolveMediaUrl } from '@/utils/media'

const MAX_AVATAR = 500 * 1024
const statusMap = { 0: '待确认', 1: '已确认', 2: '已取消', 3: '已完成', 4: '已拒绝' }
const statusKeyMap = { 0: 'pending', 1: 'confirmed', 2: 'cancelled', 3: 'done', 4: 'rejected' }
const typeKeyMap = { '月卡': 'monthly', '季卡': 'quarterly', '年卡': 'yearly', '私教': 'private' }

export default {
  name: 'MemberProfileView',
  data() {
    return {
      loading: false,
      saving: false,
      apptLoading: false,
      windowWidth: window.innerWidth,
      form: {
        username: '',
        name: '',
        phone: '',
        email: '',
        avatar: '',
        oldPassword: '',
        newPassword: '',
      },
      memberInfo: {
        typeLabel: '',
        typeKey: 'monthly',
        regDate: '',
        expireDate: '',
        active: true,
        nearExpire: false,
      },
      recentAppts: [],
    }
  },
  mounted() {
    this.load()
    this.loadRecentAppts()
    window.addEventListener('resize', this.onResize)
    window.addEventListener('gym-refresh-appointments', this.loadRecentAppts)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize)
    window.removeEventListener('gym-refresh-appointments', this.loadRecentAppts)
  },
  computed: {
    avatarDisplayUrl() {
      return resolveMediaUrl(this.form.avatar)
    },
    isMobile() {
      return this.windowWidth <= 600
    },
  },
  methods: {
    onResize() {
      this.windowWidth = window.innerWidth
    },
    todayYmd() {
      const t = new Date()
      return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`
    },
    async load() {
      this.loading = true
      try {
        const res = await api.getMemberProfileMe()
        if (res.data?.code === 200 && res.data.data) {
          const d = res.data.data
          this.form.username = d.username || ''
          this.form.name = d.name || ''
          this.form.phone = d.phone || ''
          this.form.email = d.email || ''
          this.form.avatar = d.avatar || ''

          const expireDate = d.expireDate ? String(d.expireDate).substring(0, 10) : ''
          const today = this.todayYmd()
          const statusExpired = d.status === 0
          const dateExpired = expireDate && expireDate < today
          const isExpired = statusExpired || dateExpired
          const nearExpire = !isExpired && expireDate && (() => {
            const exp = new Date(`${expireDate}T12:00:00`)
            const diffDays = Math.round((exp - new Date()) / 86400000)
            return diffDays >= 0 && diffDays <= 30
          })()

          this.memberInfo = {
            typeLabel: d.memberType || '未设置',
            typeKey: typeKeyMap[d.memberType] || 'monthly',
            regDate: d.createTime ? String(d.createTime).substring(0, 10) : (d.registerDate ? String(d.registerDate).substring(0, 10) : ''),
            expireDate: expireDate || '—',
            active: !isExpired,
            nearExpire: !!nearExpire,
          }
        } else {
          ElMessage.error(res.data?.message || '加载失败')
        }
      } catch {
        ElMessage.error('加载失败')
      } finally {
        this.loading = false
      }
    },
    async loadRecentAppts() {
      this.apptLoading = true
      try {
        const todayYmd = this.todayYmd()
        const res = await api.getAppointmentPage({
          pageNumber: 1,
          pageSize: 20,
          status: 1,
          startDate: todayYmd,
        })
        if (res.data?.code === 200) {
          const records = (res.data.data?.records || [])
            .sort((a, b) => String(a.appointmentTime).localeCompare(String(b.appointmentTime)))
            .slice(0, 5)
          this.recentAppts = records.map(a => ({
            appointmentId: a.appointmentId,
            courseName: a.courseName || `课程${a.courseId}`,
            coachName: a.coachName || `教练${a.coachId}`,
            dateStr: a.appointmentTime ? String(a.appointmentTime).substring(0, 16).replace('T', ' ') : '—',
            statusLabel: statusMap[a.status] ?? '已确认',
            statusKey: statusKeyMap[a.status] || 'confirmed',
          }))
        }
      } catch {
        // 静默失败
      } finally {
        this.apptLoading = false
      }
    },
    async onAvatarChange(file) {
      const raw = file.raw
      if (!raw) return
      if (raw.size > MAX_AVATAR) {
        ElMessage.warning('头像图片不能超过 500KB')
        return
      }
      try {
        const res = await api.uploadProfileImage(raw, 'member')
        if (res.data?.code === 200 && res.data.data?.path) {
          this.form.avatar = res.data.data.path
          ElMessage.success('头像已上传，请点击保存')
        } else {
          ElMessage.error(res.data?.message || '上传失败')
        }
      } catch {
        ElMessage.error('上传失败')
      }
    },
    async save() {
      if (!this.form.username?.trim()) {
        ElMessage.warning('用户名不能为空')
        return
      }
      const payload = {
        username: this.form.username.trim(),
        name: (this.form.name || '').trim(),
        phone: (this.form.phone || '').trim(),
        email: (this.form.email || '').trim(),
        avatar: this.form.avatar || '',
      }
      if (this.form.newPassword) {
        payload.oldPassword = this.form.oldPassword
        payload.newPassword = this.form.newPassword
      }
      this.saving = true
      try {
        const res = await api.updateMemberProfileMe(payload)
        let body = res.data
        if (body == null || typeof body !== 'object') {
          const again = await api.getMemberProfileMe()
          body = again.data
        }
        if (body?.code === 200) {
          ElMessage.success('保存成功')
          const d = body.data || {}
          this.form.oldPassword = ''
          this.form.newPassword = ''
          this.form.username = d.username ?? this.form.username
          this.form.name = d.name ?? this.form.name
          this.form.phone = d.phone ?? this.form.phone
          this.form.email = d.email ?? this.form.email
          this.form.avatar = d.avatar ?? this.form.avatar
          this.$store.commit('UPDATE_USER_FIELDS', {
            username: d.username || this.form.username,
            avatar: d.avatar || '',
          })
        } else {
          ElMessage.error(body?.message || '保存失败')
        }
      } catch {
        ElMessage.error('保存失败')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.page-wrapper {
  padding: 0;
  max-width: 560px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.profile-card {
  background: var(--bg-white);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: 28px 24px;
}
.card-desc {
  margin: 0 0 20px;
  font-size: 14px;
  color: var(--text-muted);
}

/* 套餐信息 */
.membership-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.membership-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.m-label {
  font-size: 12px;
  color: var(--text-muted);
  font-weight: 500;
}
.m-value {
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 600;
}
.near-expire {
  color: #F59E0B;
  font-weight: 700;
}
.expired-text {
  color: #EF4444;
}

/* 状态徽章 */
.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  width: fit-content;
}
.badge-active {
  background: rgba(16, 185, 129, 0.12);
  color: #10B981;
}
.badge-expired {
  background: rgba(239, 68, 68, 0.12);
  color: #EF4444;
}

/* 会员类型 tag */
.tag {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  width: fit-content;
}
.tag-monthly   { background: rgba(0,98,255,0.1);   color: #0062FF; }
.tag-quarterly { background: rgba(139,92,246,0.1); color: #8B5CF6; }
.tag-yearly    { background: rgba(16,185,129,0.1); color: #10B981; }
.tag-private   { background: rgba(245,158,11,0.1); color: #F59E0B; }

/* 预约卡 */
.card-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.link-all {
  font-size: 12px;
  color: var(--primary-blue);
  cursor: pointer;
  font-weight: 500;
}
.link-all:hover { text-decoration: underline; }
.empty-tip {
  font-size: 13px;
  color: var(--text-muted);
  text-align: center;
  padding: 16px 0;
}
.appt-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.appt-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: var(--bg-gray);
  border-radius: var(--radius-md);
}
.appt-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.dot-pending   { background: #F59E0B; }
.dot-confirmed { background: #10B981; }
.dot-cancelled { background: #9CA3AF; }
.dot-done      { background: #0062FF; }
.dot-rejected  { background: #EF4444; }

.appt-body { flex: 1; min-width: 0; }
.appt-course {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.appt-meta {
  font-size: 12px;
  color: var(--text-muted);
  margin: 0;
}
.appt-tag { flex-shrink: 0; }
.tag-status-pending   { background: rgba(245,158,11,0.1);  color: #F59E0B; }
.tag-status-confirmed { background: rgba(16,185,129,0.1);  color: #10B981; }
.tag-status-cancelled { background: rgba(107,114,128,0.1); color: #6B7280; }
.tag-status-done      { background: rgba(0,98,255,0.1);    color: #0062FF; }
.tag-status-rejected  { background: rgba(239,68,68,0.12);  color: #EF4444; }

/* 编辑表单 */
.avatar-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}
.avatar-preview {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: var(--gradient-primary, linear-gradient(135deg, #0062ff, #3d8bff));
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
  overflow: hidden;
  flex-shrink: 0;
}
.avatar-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.hint {
  margin: 6px 6px 0;
  font-size: 12px;
  color: var(--text-muted);
}
.save-item {
  margin-bottom: 0;
}

@media (max-width: 600px) {
  .profile-card {
    padding: 18px 16px;
    border-radius: var(--radius-md);
  }
  .membership-grid {
    grid-template-columns: 1fr 1fr;
    gap: 12px 10px;
  }
  :deep(.el-form-item__label) {
    font-size: 13px;
    padding-bottom: 6px;
  }
  :deep(.el-form-item) {
    margin-bottom: 16px;
  }
  .save-btn {
    width: 100%;
  }
  .save-item :deep(.el-form-item__content) {
    display: block;
  }
}
</style>
